import { t } from "i18next";
import type { JSX, ReactNode } from "react";
import { AppConfig } from "@/common/appConfig";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { V3ConfirmSheet } from "@/components/ui/v3/confirm-sheet";

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  trigger?: ReactNode;
  /** v3.2: a red confirm button (delete, discard). */
  destructive?: boolean;
  testId?: string;
}

const ConfirmDialog = ({
  open,
  onOpenChange,
  title,
  description,
  confirmText = t("components.confirmDialog.confirmText"),
  cancelText = t("components.confirmDialog.cancelText"),
  onConfirm,
  trigger,
  destructive = false,
  testId,
}: ConfirmDialogProps): JSX.Element => {
  // The v3.2 app (VITE_APP_REDESIGN_ENABLED): the v3.2 confirmation (bottom
  // sheet on phones, small card from 640px). The classic app is unchanged.
  if (AppConfig.enableAppRedesign)
    return (
      <V3ConfirmSheet
        cancelLabel={cancelText}
        confirmLabel={confirmText}
        description={description}
        destructive={destructive}
        open={open}
        testId={testId}
        title={title}
        trigger={trigger}
        onConfirm={onConfirm}
        onOpenChange={onOpenChange}
      />
    );
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      {trigger && <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>}
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel asChild>
            <Button
              variant={"outline"}
              onClick={() => {
                onOpenChange(false);
              }}
            >
              {cancelText}
            </Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              onClick={() => {
                onOpenChange(false);
                onConfirm();
              }}
            >
              {confirmText}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmDialog;
